import { useDialog } from 'contexts/dialog';
import { useViewport } from 'contexts/viewport';
import { memo } from 'react';
import { Button, FormLabel, Paper, Stack, styled, Switch, Text } from 'shell/components';

const GradingSection = memo(
    ({
        isEditable,
        form,
        setForm,
        errors,
        setPreviewUploadDialog,
        setGradingSettingsDialog,
        openVideoGradingSettings,
    }) => {
        const { setDialog, closeDialog } = useDialog();
        const { device } = useViewport();

        const getText = () => {
            if (Boolean(form.gradingCustomContext) || Boolean(form.gradingSettings)) {
                return `Custom settings ${errors.gradingSettings || ''}`.trim();
            }

            return 'System default';
        };

        return (
            <Stack sx={{ width: '100%' }} spacing={1}>
                <Text variant={'p1'}>Grading</Text>
                <CustomPaper variant={'outlined'}>
                    <Stack spacing={4}>
                        <CustomPaper variant={'outlined'}>
                            <Stack spacing={3}>
                                <Stack
                                    alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                    direction={'row'}
                                    justifyContent={'space-between'}
                                >
                                    <Stack
                                        alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                        direction={device === 'mobile' ? 'column-reverse' : 'row'}
                                        spacing={device === 'mobile' ? 2 : 4}
                                    >
                                        <Text variant={'p1'}>Grading Settings</Text>
                                        {/* The video doesn't exist yet, so this button is hidden for now */}
                                        {/* <Button
                                        startIcon={<Icon kind={'video'} />}
                                        variant={'borderless'}
                                        onClick={() => openVideoGradingSettings()}
                                    >
                                        Watch a Tutorial
                                    </Button> */}
                                    </Stack>
                                    <Button
                                        disabled={!isEditable}
                                        variant={'borderless'}
                                        onClick={() => setGradingSettingsDialog({ open: true })}
                                    >
                                        Advanced Settings
                                    </Button>
                                </Stack>
                                <Text color={errors.gradingSettings ? 'error' : 'light'}>{getText()}</Text>
                            </Stack>
                        </CustomPaper>
                        <Stack alignItems={'center'} direction={'row'} spacing={4}>
                            <FormLabel id={'grading-switch'}>
                                <Text variant={'p1'}>Grading Suggestions</Text>
                            </FormLabel>
                            <Switch
                                aria-labelledby={'grading-switch'}
                                checked={form.aiGradingEnabled}
                                disabled={!isEditable}
                                onChange={(_, value) => {
                                    if (form.feedbackSettings.structure === 'templated') {
                                        setDialog({
                                            open: true,
                                            message: 'Disabling Grading will Reset Feedback to Default Settings',
                                            description:
                                                'Smart Feedback Templates are currently selected, which requires grading to be enabled. If you disable grading, feedback will be changed to the system default. Do you want to continue?',
                                            note: '',
                                            variant: 'destructive',
                                            actionButtonText: 'Continue',
                                            cancelButtonText: 'Cancel',
                                            onAction: () => {
                                                closeDialog();
                                                setForm((current) => ({
                                                    ...current,
                                                    aiGradingEnabled: value,
                                                    feedbackSettings: {
                                                        ...current.feedbackSettings,
                                                        structure: 'system',
                                                    },
                                                }));
                                            },
                                            onCancel: () => closeDialog(),
                                            onClose: () => closeDialog(),
                                        });
                                    } else {
                                        setForm((current) => ({ ...current, aiGradingEnabled: value }));
                                    }
                                }}
                            />
                        </Stack>
                        <Text>
                            TimelyGrader leverages assignment details to provide instructors with personalized grading
                            suggestions for student’s final submissions.
                        </Text>
                        <Button
                            sx={{ alignSelf: 'flex-end' }}
                            disabled={
                                !form.rubric || errors.baseline || form.feedbackSettings.structure === 'templated'
                            }
                            variant={'secondary'}
                            onClick={() => setPreviewUploadDialog({ open: true, target: 'grading' })}
                        >
                            Preview
                        </Button>
                        {form.feedbackSettings.structure === 'templated' && (
                            <Text align={'right'} color={'light'}>
                                Preview is unavailable for Smart Feedback Templates
                            </Text>
                        )}
                    </Stack>
                </CustomPaper>
            </Stack>
        );
    }
);

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

export default GradingSection;
